import {useEffect} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';

/** How long without a key press or click before the lock is left to lapse. */
export const HOLD_LOCK_IDLE_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ['keydown', 'pointerdown'] as const;

/**
 * Holds the document's realtime editor lock while mounted and the user is active. Editing a draft
 * writes nothing to the document, so nothing else renews the lock, and another user could take it once
 * it expires. Checks at a quarter of the lock's duration: a renewal only goes out once less than half
 * the lock's lifetime remains, so checking at half could skip a renewal until the lock expired.
 *
 * After `HOLD_LOCK_IDLE_MS` without a key press or click, renewal stops and the lock lapses, so an
 * abandoned editor doesn't keep collaborators out of the document. The next key press or click
 * acquires it again. This also covers a background tab, where the browser can throttle the renewal
 * timer past the lock's duration.
 */
export default function useHoldDocumentLock(documentId: string | null): void {
  useEffect(() => {
    if (documentId == null) {
      return undefined;
    }
    // Locking a worksheet step also locks its sibling steps, and each lock must be released.
    const lockedIds = new Set([documentId]);
    const acquire = (): void => {
      DocumentEditorLockUtil.forceAcquireLocks(documentId).forEach((id) => lockedIds.add(id));
    };
    let lastActivityAt = Date.now();
    const isIdle = (): boolean => Date.now() - lastActivityAt >= HOLD_LOCK_IDLE_MS;
    const onActivity = (): void => {
      const wasIdle = isIdle();
      lastActivityAt = Date.now();
      if (wasIdle) {
        acquire();
      }
    };

    acquire();
    const renewal = setInterval(
      () => {
        if (!isIdle()) {
          acquire();
        }
      },
      Math.max(BenchlingConfig.ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS / 4, 1) * 1000
    );
    ACTIVITY_EVENTS.forEach((type) => {
      window.addEventListener(type, onActivity, {capture: true});
    });
    return () => {
      clearInterval(renewal);
      ACTIVITY_EVENTS.forEach((type) => {
        window.removeEventListener(type, onActivity, {capture: true});
      });
      // `forceAcquireLocks` leaves a tombstone on each document that makes `releaseLocks` a no-op; clear
      // them first. After a Save the document has unsaved changes, so `releaseLocks` does nothing and the
      // autosave releases the locks once it saves.
      lockedIds.forEach((id) => {
        DocumentEditorLockUtil.clearReleaseLockTimeout(id);
      });
      DocumentEditorLockUtil.releaseLocks([...lockedIds]);
    };
  }, [documentId]);
}
